<script setup lang="ts">
import LangRuIcon from '@/shared/ui/Icons/FlagRu.vue';
import LangEnIcon from '@/shared/ui/Icons/FlagEn.vue';
import LangUzIcon from '@/shared/ui/Icons/FlagUz.vue';
import { RU_CODE, EN_CODE, UZ_CODE } from '@/shared/constants/localeCodes';
import type { Locales } from '@/shared/model/types/locale';
import { useLocaleSlugSwitch } from '@/shared/composables/useLocaleSlugSwitch';

const { locale, setLocale, t } = useI18n();
const { switchToLocale } = useLocaleSlugSwitch();

const isShowDropdown = ref(false);

const languages = [
  { id: 1, code: RU_CODE, name: 'Русский', icon: LangRuIcon },
  { id: 2, code: UZ_CODE, name: "O'zbek", icon: LangUzIcon },
  { id: 3, code: EN_CODE, name: 'English', icon: LangEnIcon },
];

const currentLang = computed(() => languages.find(i => i.code === locale.value));

const availableLocales = computed(() => {
  return languages.filter(l => l.code !== currentLang.value?.code);
});

const selectLang = async (code: Locales) => {
  setLocale(code);
  await switchToLocale(code);
  isShowDropdown.value = false;
};

const closeDropdown = () => {
  isShowDropdown.value = false;
};

const toggleDropdown = () => {
  isShowDropdown.value = !isShowDropdown.value;
};
</script>

<template>
  <div v-click-outside="closeDropdown" class="lang-button">
    <button
      class="lang-button__button"
      :aria-label="t('selectLanguage')"
      @click.prevent="toggleDropdown"
    >
      <component :is="currentLang?.icon" class="lang-button__icon" />
      <span class="lang-button__text">{{ currentLang?.name }}</span>
    </button>
    <transition name="fade" mode="out-in">
      <div v-if="isShowDropdown" class="lang-button__dropdown">
        <button
          v-for="l in availableLocales"
          :key="l.id"
          class="lang-button__dropdown-item"
          @click="selectLang(l.code as Locales)"
        >
          <component :is="l.icon" class="lang-button__dropdown-icon" />
          <span>{{ l.name }}</span>
        </button>
      </div>
    </transition>
  </div>
</template>

<style lang="less">
.lang-button {
  display: inline-flex;
  position: relative;

  &__button {
    .button-reset();

    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 15px;
    font-weight: 400;
    line-height: 100%;
    color: @color-oxford-blue;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    min-width: 140px;
    background: #fff;
    box-shadow: 0 2px @size-x2 rgba(0, 0, 0, 0.27);
    border-radius: @size-x3;
    padding: @size-x2 0;
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__dropdown-item {
    .button-reset();

    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: 100%;
    font-size: 15px;
    font-weight: 400;
    line-height: 100%;
    color: @color-oxford-blue;
    cursor: pointer;
    padding: @size-x2 @size-x5;
    transition: background 0.15s;
    text-decoration: none;
  }

  &__dropdown-item:hover {
    background: @color-athens-gray-dark;
  }

  &__icon {
    width: @size-x3;
    height: @size-x3;
  }

  @media @1024 {
    &__dropdown {
      top: @size-x6;
    }
  }
}
</style>
